import { useCallback, ChangeEvent } from 'react';

import { Box } from '@twilio-paste/core/box';
import { Button } from '@twilio-paste/core/button';
import { useUIDSeed } from '@twilio-paste/core/dist/uid-library';
import { FormControl, FormSection, FormSectionHeading } from '@twilio-paste/core/form';
import { Input } from '@twilio-paste/core/input';
import { Label } from '@twilio-paste/core/label';

import { InfoIcon } from '../../../common/InfoIcon';

import { useTargetAuthHeaderContext } from './TargetAuthHeaderContext';
import { TargetAuthHeaderTable } from './TargetAuthHeaderTable';

export function TargetAuthHeaderSection() {
  const seed = useUIDSeed();
  const {
    addAuthHeader,
    authHeaders,
    authHeaderFormItem = {},
    setAuthHeaderFormItem,
    readOnly,
  } = useTargetAuthHeaderContext();

  const handleAuthHeaderChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setAuthHeaderFormItem({ ...authHeaderFormItem, name: event.target.value });
    },
    [authHeaderFormItem, setAuthHeaderFormItem],
  );

  const handleAuthHeaderValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setAuthHeaderFormItem({ ...authHeaderFormItem, value: event.target.value });
    },
    [authHeaderFormItem, setAuthHeaderFormItem],
  );

  const handleAddAuthHeader = useCallback(() => {
    addAuthHeader({ ...authHeaderFormItem, editing: false });
    setAuthHeaderFormItem({ name: '', value: '' });
  }, [addAuthHeader, authHeaderFormItem, setAuthHeaderFormItem]);

  return (
    <FormSection>
      {!readOnly && (
        <Box display="flex" flexDirection="column" rowGap="space60">
          <FormSectionHeading>
            <Box display="flex" alignItems="center" columnGap="space10">
              Target Authentication
              <InfoIcon tooltipText="Provide the target application authentication values." />
            </Box>
          </FormSectionHeading>
          <FormControl>
            <Label htmlFor={seed('authHeader')} required>
              Auth Header
            </Label>
            <Input
              name="authHeader"
              type="text"
              id={seed('authHeader')}
              placeholder="Auth Header"
              readOnly={readOnly}
              value={authHeaderFormItem?.name}
              onChange={handleAuthHeaderChange}
              disabled={authHeaderFormItem?.editing}
            ></Input>
          </FormControl>
          <FormControl>
            <Label htmlFor={seed('authHeaderValue')} required>
              Auth Header Value
            </Label>
            <Input
              name="authHeaderValue"
              type="password"
              id={seed('authHeaderValue')}
              placeholder="Auth Header Value"
              readOnly={readOnly}
              value={authHeaderFormItem?.value}
              onChange={handleAuthHeaderValueChange}
            />
          </FormControl>
          <FormControl>
            <Box display="flex" columnGap="space20">
              <Button
                variant="secondary"
                onClick={handleAddAuthHeader}
                disabled={!authHeaderFormItem?.name || !authHeaderFormItem?.value}
              >
                {authHeaderFormItem?.editing ? 'Update' : 'Add'}
              </Button>
            </Box>
          </FormControl>
        </Box>
      )}
      <FormControl>
        <TargetAuthHeaderTable authHeaders={authHeaders} />
      </FormControl>
    </FormSection>
  );
}
